import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatDistanceToNow } from "date-fns";
import type { PropertyOffer } from "@/types/buyer";
import { DollarSign, Calendar, MapPin, Building2 } from "lucide-react";

interface OfferCardProps {
  offer: PropertyOffer;
  onViewDetails: () => void;
}

export const OfferCard = ({ offer, onViewDetails }: OfferCardProps) => {
  const property = offer.property;
  const buyer = offer.buyer_profile;
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400';
      case 'accepted':
        return 'bg-green-500/10 text-green-600 dark:text-green-400';
      case 'rejected':
        return 'bg-red-500/10 text-red-600 dark:text-red-400';
      case 'withdrawn':
        return 'bg-gray-500/10 text-gray-600 dark:text-gray-400';
      default:
        return '';
    }
  };

  const calculatePercentageDiff = () => {
    if (!property?.price) return null;
    const diff = ((offer.offer_amount - property.price) / property.price) * 100;
    return diff.toFixed(1);
  };

  const percentDiff = calculatePercentageDiff();
  
  const daysUntilClose = offer.close_date ? 
    Math.ceil((new Date(offer.close_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : 
    null;

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardContent className="p-4">
        <div className="flex gap-4">
          {/* Property thumbnail */}
          <div className="flex-shrink-0">
            <img 
              src={property?.image_url || '/placeholder.svg'} 
              alt={property?.address || 'Property'}
              className="w-24 h-24 object-cover rounded-lg"
            />
          </div>

          {/* Offer details */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-base truncate">
                  {property?.address}
                </h4>
                <p className="text-sm text-muted-foreground">
                  {property?.city}, {property?.state} {property?.zip_code}
                </p>
              </div>
              <Badge className={getStatusColor(offer.status)} variant="secondary">
                {offer.status.charAt(0).toUpperCase() + offer.status.slice(1)}
              </Badge>
            </div>

            {/* Buyer info */}
            <div className="flex items-center gap-2 mb-3">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">
                {buyer?.full_name}
                {buyer?.company_name && <span className="text-muted-foreground"> • {buyer.company_name}</span>}
              </span>
            </div>

            {/* Offer amount and comparison */}
            <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-3">
              <div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
                  <DollarSign className="h-3 w-3" />
                  <span>Offer Amount</span>
                </div>
                <p className="font-bold text-lg text-godyspo-gold">
                  ${offer.offer_amount.toLocaleString()}
                </p>
                {percentDiff && (
                  <p className={`text-xs ${parseFloat(percentDiff) < 0 ? 'text-red-500' : 'text-green-500'}`}>
                    {parseFloat(percentDiff) > 0 ? '+' : ''}{percentDiff}% vs asking
                  </p>
                )}
              </div>

              <div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
                  <Calendar className="h-3 w-3" />
                  <span>Close Date</span>
                </div>
                <p className="font-medium">
                  {new Date(offer.close_date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </p>
                {daysUntilClose !== null && (
                  <p className="text-xs text-muted-foreground">
                    {daysUntilClose} days from now
                  </p>
                )}
              </div>
            </div>

            {/* Buyer notes preview */}
            {offer.buyer_notes && (
              <div className="mb-3">
                <p className="text-sm text-muted-foreground line-clamp-2">
                  "{offer.buyer_notes}"
                </p>
              </div>
            )}

            {/* Footer */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground">
                Submitted {formatDistanceToNow(new Date(offer.created_at), { addSuffix: true })}
              </span>
              <Button size="sm" variant="outline" onClick={onViewDetails}>
                View Details
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
